import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { isErrorExistingFromGQLResponse } from '@jepto/shared/utils/collection';

import { mutationCreatePalette } from '../graphql/mutations/create-palette';
import { mutationDeletePalette } from '../graphql/mutations/delete-palette';
import { mutationUpdatePalette } from '../graphql/mutations/update-palette';

export interface CreatePaletteFieldInput {
	label: string;
	light: string[];
	dark: string[];
}

export interface CreatePaletteItemResult {
	paletteId: string;
	label: string;
	light: string[];
	dark: string[];
}

export interface UpdatePaletteInput {
	readonly paletteId: string;
	readonly label: string;
	readonly light: string[];
	readonly dark: string[];
}

@Injectable({ providedIn: 'root' })
export class JepPaletteMutationsService {
	private readonly jepRequest = inject(JepRequestService);

	async createPalette(
		input: CreatePaletteFieldInput
	): Promise<CreatePaletteItemResult> {
		const variables = {
			input: {
				label: input.label,
				light: input.light,
				dark: input.dark,
			},
		};

		const gqlResponse = await firstValueFrom(
			this.jepRequest.mutate({
				mutation: mutationCreatePalette,
				variables,
			})
		);

		if (isErrorExistingFromGQLResponse(gqlResponse)) {
			const msg =
				(Array.isArray(gqlResponse.errors) &&
					gqlResponse.errors[0]?.message) ??
				'Could not save palette';
			throw new Error(msg);
		}

		const payload = gqlResponse?.data?.createPalette as
			| CreatePaletteItemResult
			| undefined;
		if (!payload?.paletteId) {
			throw new Error('createPalette: missing palette in response');
		}

		return payload;
	}

	async updatePalette(
		input: UpdatePaletteInput
	): Promise<CreatePaletteItemResult> {
		const variables = {
			input: {
				id: input.paletteId,
				patch: {
					label: input.label,
					light: input.light,
					dark: input.dark,
				},
			},
		};

		const gqlResponse = await firstValueFrom(
			this.jepRequest.mutate({
				mutation: mutationUpdatePalette,
				variables,
			})
		);

		if (isErrorExistingFromGQLResponse(gqlResponse)) {
			const msg =
				(Array.isArray(gqlResponse.errors) &&
					gqlResponse.errors[0]?.message) ??
				'Could not update palette';
			throw new Error(msg);
		}

		const ok = gqlResponse?.data?.updatePalette?.success === true;
		if (!ok) {
			throw new Error('Could not update palette');
		}

		return {
			paletteId: input.paletteId,
			label: input.label,
			light: input.light,
			dark: input.dark,
		};
	}

	async deletePalette(params: { readonly paletteId: string }): Promise<void> {
		const variables = {
			paletteId: params.paletteId,
		};

		const gqlResponse = await firstValueFrom(
			this.jepRequest.mutate({
				mutation: mutationDeletePalette,
				variables,
			})
		);

		if (isErrorExistingFromGQLResponse(gqlResponse)) {
			const msg =
				(Array.isArray(gqlResponse.errors) &&
					gqlResponse.errors[0]?.message) ??
				'Could not delete palette';
			throw new Error(msg);
		}

		const ok = gqlResponse?.data?.deletePalette === true;
		if (!ok) {
			throw new Error('deletePalette: server did not confirm deletion');
		}
	}
}
